import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Checkbox,
  Frame,
  GroupBox,
  Radio,
  ScrollView,
  Select,
  Window,
  WindowContent,
  WindowHeader
} from 'react95';
import { setBulkEditMode, setBulkEditTasks } from '../../actions/taskListActions';
import { ALL_STAGES, SUBSTAGE_FIELDS } from '../../global/Environment';
import { bulkEditModeSelector, bulkEditTasksSelector } from '../../selectors/taskListSelectors';
import { getStageDisplayName, prepareUserDropdownList } from '../../utils/taskUtils';
import './BulkEditModal.scss';
import { useQDeckUserEmailNameMap } from '../../api/qdeckApi';
import { useBulkUpdateTasksMutation } from '../../api/opsSystemApi';
import { omitBy } from 'lodash-es';

const BulkEditModal = ({ tasks, areTasksLoading }) => {
  const dispatch = useDispatch();

  const [fieldUpdates, setFieldUpdates] = useState({});
  const [substageUpdates, setSubstageUpdates] = useState({});

  const selectedTaskIds = useSelector(bulkEditTasksSelector);
  const bulkEditMode = useSelector(bulkEditModeSelector);

  const { qdeckUserEmailNameMap, isLoading: isQDeckUserEmailNameMapLoading } = useQDeckUserEmailNameMap();

  const [bulkUpdateTasks] = useBulkUpdateTasksMutation();

  const wereSubstagesUpdated = useMemo(() => {
    return Object.keys(substageUpdates).some((substage) => substageUpdates[substage] !== null);
  }, [substageUpdates]);

  const isFormEmpty = useMemo(
    () => !Object.keys(fieldUpdates).filter((field) => fieldUpdates[field]).length && !wereSubstagesUpdated,
    [fieldUpdates, wereSubstagesUpdated]
  );

  if (areTasksLoading || isQDeckUserEmailNameMapLoading) {
    return null;
  }

  const validUpdateStages = ALL_STAGES.filter((stage) => !['user escalation', 'Purgatory'].includes(stage));

  const submitForm = () => {
    bulkUpdateTasks({
      selectedTaskIds,
      ...fieldUpdates,
      substages: omitBy(substageUpdates, (value) => value === null)
    });
    dispatch(setBulkEditTasks([]));
    dispatch(setBulkEditMode(null));
  };

  return (
    <Window className='bulk-edit-modal'>
      <WindowHeader className='bulk-edit-modal-header'>
        <span>Override update tasks</span>
        <Button
          primary
          square
          size='sm'
          onClick={() => {
            dispatch(setBulkEditTasks([]));
            dispatch(setBulkEditMode(null));
          }}>
          X
        </Button>
      </WindowHeader>

      <WindowContent className='bulk-edit-modal-body'>
        <ScrollView className='bulk-edit-modal-body-scroll'>
          <div className='bulk-edit-modal-body-row'>
            <GroupBox
              label='Assignee'
              className='
            bulk-edit-modal-body-row-groupbox
            '>
              <Select
                value={fieldUpdates.assignee || ''}
                options={prepareUserDropdownList(qdeckUserEmailNameMap)}
                width='100%'
                menuMaxHeight={300}
                onChange={(opt) => setFieldUpdates({ ...fieldUpdates, assignee: opt.value })}
                disabled={!!fieldUpdates.unassign}
              />

              <Checkbox
                label='Unassign'
                checked={!!fieldUpdates.unassign}
                onChange={() =>
                  setFieldUpdates(
                    !fieldUpdates.unassign
                      ? { ...fieldUpdates, unassign: true, assignee: null }
                      : { ...fieldUpdates, unassign: false }
                  )
                }
              />
            </GroupBox>
          </div>

          <div className='bulk-edit-modal-body-row'>
            <GroupBox label='Stage'>
              <Select
                value={fieldUpdates.stage || ''}
                options={['placeholder', ...validUpdateStages].map((stage) =>
                  stage === 'placeholder'
                    ? { label: '', value: '' }
                    : {
                        label: getStageDisplayName(stage),
                        value: stage
                      }
                )}
                width='100%'
                menuMaxHeight={300}
                onChange={(opt) => setFieldUpdates({ ...fieldUpdates, stage: opt.value })}
              />
            </GroupBox>
          </div>

          <GroupBox label='Substages'>
            {SUBSTAGE_FIELDS.map((substage) => (
              <GroupBox key={substage.name} label={substage.label}>
                <Radio
                  checked={!(substage.name in substageUpdates) || substageUpdates[substage.name] === null}
                  onChange={() => setSubstageUpdates({ ...substageUpdates, [substage.name]: null })}
                  label='No change'
                  name={substage.name}
                />
                <Radio
                  checked={substageUpdates[substage.name]}
                  onChange={() => setSubstageUpdates({ ...substageUpdates, [substage.name]: true })}
                  label='True'
                  name={substage.name}
                />
                <Radio
                  checked={substageUpdates[substage.name] === false}
                  onChange={() => setSubstageUpdates({ ...substageUpdates, [substage.name]: false })}
                  label='False'
                  name={substage.name}
                />
              </GroupBox>
            ))}
          </GroupBox>
        </ScrollView>

        <Button primary fullWidth size='lg' disabled={isFormEmpty} onClick={submitForm}>
          Submit
        </Button>
      </WindowContent>

      <Frame variant='status' className='bulk-edit-modal-footer'>
        {bulkEditMode === 'all'
          ? `Editing all current Task List tasks (${tasks.length} total)`
          : `Editing ${selectedTaskIds.length} selected task(s)`}
      </Frame>
    </Window>
  );
};

export default BulkEditModal;
